import { ReactNode } from "react";

import Box from "@mui/material/Box";
import { QuestionType } from "@simplyk/common";

import { Translate } from "@/common/hooks/useTranslate";
import { Checkbox, CheckboxMultiple, Date, Location, Mail, Phone, Success, TextInput } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

export const getQuestionTypeIcon = (type: QuestionType): IconElement => {
  switch (type) {
    case QuestionType.Simple:
      return <TextInput />;
    case QuestionType.Multiple:
      return <Success />;
    case QuestionType.MultipleCheckboxes:
      return <CheckboxMultiple />;
    case QuestionType.Checkbox:
      return <Checkbox />;
    case QuestionType.Email:
      return <Mail />;
    case QuestionType.PhoneNumber:
    case QuestionType.NotificationPhoneNumber:
      return <Phone />;
    case QuestionType.Date:
      return <Date />;
    case QuestionType.Name:
      return <TextInput />;
    case QuestionType.Address:
      return <Location />;
    default:
      return <TextInput />;
  }
};

export type SelectableQuestionType = Exclude<
  QuestionType,
  QuestionType.Utm | QuestionType.NotificationPhoneNumber | QuestionType.Address
>;

export type QuestionTypeConfig = { icon: IconElement; label: string };

export const getQuestionTypeConfig = (t: Translate): Record<SelectableQuestionType, QuestionTypeConfig> => ({
  [QuestionType.Simple]: {
    icon: <TextInput />,
    label: t("dashboard", "donationForm.simpleQuestion"),
  },
  [QuestionType.Multiple]: {
    icon: <Success />,
    label: t("dashboard", "donationForm.uniqueQuestion"),
  },
  [QuestionType.MultipleCheckboxes]: {
    icon: <CheckboxMultiple />,
    label: t("dashboard", "donationForm.multipleQuestion"),
  },
  [QuestionType.Checkbox]: {
    icon: <Checkbox />,
    label: t("dashboard", "common.checkbox"),
  },
  [QuestionType.Email]: {
    icon: <Mail />,
    label: t("dashboard", "donationForm.questionEmail"),
  },
  [QuestionType.PhoneNumber]: {
    icon: <Phone />,
    label: t("dashboard", "donationForm.questionPhoneNumber"),
  },
  [QuestionType.Date]: {
    icon: <Date />,
    label: t("dashboard", "donationForm.questionDate"),
  },
  [QuestionType.Name]: {
    icon: <TextInput />,
    label: t("dashboard", "donationForm.questionName"),
  },
});

export const getQuestionTypeRendererConfig = (t: Translate, type: QuestionType): QuestionTypeConfig | undefined => {
  if (type === QuestionType.NotificationPhoneNumber) {
    return { icon: <Phone />, label: t("dashboard", "donationForm.questionPhoneNumber") };
  }
  if (type === QuestionType.Address) {
    return { icon: <Location />, label: t("dashboard", "stepQuestionsV4.dialog.typeAddress") };
  }
  const selectable = getQuestionTypeConfig(t);
  if (type in selectable) {
    return selectable[type as SelectableQuestionType];
  }
  return undefined;
};

// Only the type label stays italic (inherited from the container); choice values render in regular style.
export const getQuestionTypeSubtitle = (
  t: Translate,
  type: QuestionType,
  choices?: { body: string }[]
): ReactNode | undefined => {
  const typeLabel = getQuestionTypeRendererConfig(t, type)?.label;
  if (!typeLabel) {
    return undefined;
  }
  if (!choices || choices.length === 0) {
    return typeLabel;
  }
  return (
    <>
      {typeLabel}
      <Box component="span" sx={{ fontStyle: "normal" }}>
        {` • ${choices.map((choice) => choice.body).join(", ")}`}
      </Box>
    </>
  );
};
